import React, { useState } from 'react';
import styled from 'styled-components';
import { translate } from 'translations/i18n';
import {
  Text,
  ButtonIcon,
  ButtonCondensed,
  Menu,
  MenuItem,
  Spinner,
} from '@companycam/slab-web';
import DeleteModal from 'components/shared/DeleteModal';
import ReadOnlyTipTap from './ReadOnlyTipTap';

import { formatDate } from 'helpers/intl-helper';
import { useGetPageTemplate } from 'javascripts/data/v3';
import { PageTemplateId } from 'data/v3/templates/pages/types';

const PageHeader = styled.div`
  position: sticky;
  top: 0;
  padding: var(--cc_size_spacing_s) var(--cc_size_spacing_m);
  background-color: white;
  border-bottom-width: var(--cc_size_border_width_s);
  border-bottom-style: solid;
  border-bottom-color: var(--cc_color_border_default);
  z-index: 200;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const PageStatusDiv = styled.div`
  padding: 0 var(--cc_size_spacing_s);
  margin-left: auto;
`;

export type PageTemplatePreviewProps = {
  pageTemplateId: PageTemplateId;
  onCreateFromTemplate: (templateId: PageTemplateId) => void;
  onDelete: (deletedId: PageTemplateId) => void;
  onClose: () => void;
};

const PageTemplatePreview = ({
  pageTemplateId,
  onCreateFromTemplate,
  onDelete,
  onClose,
}: PageTemplatePreviewProps) => {
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);

  const { pageTemplate, isLoading } = useGetPageTemplate({
    pageTemplateDbId: pageTemplateId,
  });

  if (isLoading) {
    return (
      <Spinner
        accessibilityLabel="page-template-preview-spinner"
        size="large"
        style={{
          margin: 'auto',
          marginTop: 'var(--cc_size_spacing_l)',
        }}
      />
    );
  }

  return (
    <>
      <PageHeader>
        <ButtonCondensed
          icon={{
            name: 'chevron-left',
            position: 'before',
          }}
          onClick={onClose}
        >
          {translate('common.back')}
        </ButtonCondensed>
        <PageStatusDiv>
          <Text as="span" color="subtle" size="xs">
            {pageTemplate?.updatedAt
              ? `${translate('common.edited')} ${formatDate(
                  new Date(pageTemplate.updatedAt),
                  'MMM d h:mm:ss a',
                )}`
              : null}
          </Text>
        </PageStatusDiv>

        <Menu
          triggerButton={
            <ButtonIcon
              accessibilityLabel="Dropdown arrow"
              iconName="dots-horizontal"
              size="small"
            />
          }
        >
          <MenuItem
            iconName="file-document-plus-outline"
            onClick={() => onCreateFromTemplate(pageTemplateId)}
          >
            {translate('pageTemplates.preview.createFromTemplate')}
          </MenuItem>
          <MenuItem
            iconName="pencil-outline"
            onClick={() => {
              window.Turbo.visit(`/templates/pages/${pageTemplateId}/edit`);
            }}
          >
            {translate('pageTemplates.preview.editTemplate')}
          </MenuItem>
          <MenuItem
            iconName="trash-can-outline"
            onClick={() => setDeleteModalIsOpen(true)}
          >
            {translate('common.delete')}
          </MenuItem>
        </Menu>
      </PageHeader>

      <ReadOnlyTipTap pageTemplate={pageTemplate} />
      <DeleteModal
        isOpen={deleteModalIsOpen}
        onClose={() => setDeleteModalIsOpen(false)}
        onDelete={() => onDelete(pageTemplateId as PageTemplateId)}
        title={translate('useTemplateModal.deleteModal.title')}
        message={translate('useTemplateModal.deleteModal.body')}
      />
    </>
  );
};

export default PageTemplatePreview;
